import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Divider,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MicIcon from '@mui/icons-material/Mic';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { differenceInYears } from 'date-fns';
import { useAuthStore } from '../../store/authStore';
import { formatDateOnly } from '../../utils/dateTime';
import { patientDetailsApi, practiceFusionApi } from '../../services/api';
import PatientOverview from './PatientOverview';
import { usePatientSectionCollapse } from './usePatientSectionCollapse';
import { usePatientClinical } from './usePatientClinical';

type PatientDetailsViewProps = {
  spruceEntityId?: string;
  patientName?: string;
  patientPhone?: string;
  patientEmail?: string;
  patientDob?: string;
  patientGender?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  /** Back-button text — e.g. "Back to task" when arriving from a triage task. */
  backLabel?: string;
  // When provided (e.g. from the Contacts panel) the full contact record
  // is rendered as a 3-card info block above the Clinical Snapshot, so the
  // doctor sees identity / address / admin metadata on the same page they
  // read the chart from.
  contact?: any;
  onEditContact?: () => void;
  onOpenMessages?: () => void;
  // Jump to the AI Consultation tab and open a specific thread, used by the
  // compact AI consultation markers in the timeline.
  onOpenConsultation?: (threadId: string) => void;
  // Jump to the History tab (e.g. the "View all in History" encounters link).
  onOpenHistory?: () => void;
  // Controls which portion of the view renders: 'all' (default, standalone
  // page), 'details' (clinical snapshot only), 'documents' (Consolidated
  // Summary + Documents module), or 'history' (the timeline only). Lets the
  // Contacts panel show each module under its own dedicated tab.
  // 'chart' = the clinical snapshot only (no Additional Info) — used by the
  // desktop Open Chart drawer so it mirrors the Details snapshot from any tab.
  view?: 'all' | 'details' | 'documents' | 'history' | 'chart';
  /** Open this document on the Documents tab (Faxes filed deep link). */
  initialDocId?: string;
};

type PatientDetail = {
  id?: string;
  category?: string;
  content?: string;
  createdAt?: string;
  created_at?: string;
  // 'practice_fusion' when this entry came from the PF import fallback rather
  // than a manual chart entry — drives the provenance tag in the snapshot.
  source?: string;
};

type PatientDocument = {
  id: string;
  filename: string;
  content_type?: string;
  file_size?: number;
  status?: string;
  created_at?: string;
  createdAt?: string;
  summary_text?: string;
  uploaded_by?: string | null;
  uploaded_by_user_id?: number | null;
};

type HistoryEvent = {
  id: string;
  event_type?: string;
  event_source?: string;
  // For chart-source events this is the underlying patient_details.category
  // (e.g. 'vitals_weight', 'allergies') so the UI can apply category-aware
  // formatting (unit conversion, etc.).
  event_category?: string;
  event_title?: string;
  event_body?: string;
  event_body_full?: string;
  author?: string | null;
  author_id?: number | null;
  event_date?: string;
  created_at?: string;
  updated_at?: string;
  metadata?: any;
  // True when this entry is a "graduated" AI action (e.g. an appointment
  // created from an AI scheduling suggestion); drives the "AI-assisted" badge.
  ai_assisted?: boolean;
};

// Number of timeline entries shown initially and added per "Show more" click.
const HISTORY_PAGE_SIZE = 10;

// Single source of truth for the History timeline categories. Each entry drives
// the filter chip (label + count), the per-item source badge (color + text),
// and "All"-view exclusion — so adding a category is a one-line change here.
type TimelineSource = {
  key: string;
  label: string; // filter-chip label
  color: string; // MUI sx color token or hex, used for the source badge
  badgeLabel?: string; // per-item pill text (defaults to key)
  core?: boolean; // always show the filter chip, even at count 0
  aiOnly?: boolean; // hidden from the default "All" view
};
const TIMELINE_SOURCES: TimelineSource[] = [
  { key: 'spruce', label: 'Messages', color: 'primary.main', core: true },
  // Notes = internal clinician notes (composed via the Messages tab's Note
  // mode) — named to match the composer toggle. Chart Edits = actual chart
  // field edits only.
  { key: 'note', label: 'Notes', color: '#7c3aed' },
  // Appointments = scheduling (no `core`, so it stays hidden until scheduling
  // data exists). Encounters = documented clinical visits — named to match the
  // hero pill, the panel, and the AI dossier.
  { key: 'calendar', label: 'Appointments', color: 'success.main' },
  { key: 'encounter', label: 'Encounters', color: 'secondary.main', badgeLabel: 'visit' },
  { key: 'document', label: 'Documents', color: 'warning.main' },
  { key: 'diagnosis', label: 'Diagnoses', color: '#4f46e5', badgeLabel: 'dx' },
  { key: 'granola', label: 'Granola', color: '#0d9488' },
  { key: 'chart', label: 'Chart Edits', color: 'info.main', core: true },
  { key: 'ai', label: 'AI', color: 'secondary.main', aiOnly: true },
];
const TIMELINE_SOURCE_MAP: Record<string, TimelineSource> = Object.fromEntries(
  TIMELINE_SOURCES.map((s) => [s.key, s]),
);
const AI_ONLY_KEYS = new Set(TIMELINE_SOURCES.filter((s) => s.aiOnly).map((s) => s.key));

const PatientDetailsView: React.FC<PatientDetailsViewProps> = ({
  spruceEntityId,
  patientName,
  patientPhone,
  patientEmail,
  patientDob,
  patientGender,
  showBackButton = false,
  onBack,
  backLabel = 'Back',
  contact,
  onEditContact,
  onOpenMessages,
  onOpenConsultation,
  onOpenHistory,
  view = 'all',
  initialDocId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const MAX_RECORD_SECONDS = 60 * 60;
  const [patient, setPatient] = useState<any>(null);
  const [details, setDetails] = useState<PatientDetail[]>([]);
  // Practice Fusion fallback clinical entries (allergies / meds / dx / family
  // history). Kept separate from `details` (which is the manual patient_details
  // chart) and merged only into the Clinical Snapshot input below, so PF data
  // surfaces the snapshot exactly like it already lights the top chip strip —
  // without polluting the edit-history timeline with non-revision rows.
  const [pfDetails, setPfDetails] = useState<PatientDetail[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  // Shared read-only clinical journey data (encounters, diagnoses, granola
  // notes) — one fetch, consumed by both the timeline and the clinical cards.
  const {
    encounters,
    diagnoses: clinicalDiagnoses,
    granolaNotes,
    loading: loadingClinical,
  } = usePatientClinical(spruceEntityId);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(initialDocId || null);

  useEffect(() => {
    if (initialDocId) setSelectedDocId(initialDocId);
  }, [initialDocId]);
  const selectedDocIdRef = useRef<string | null>(selectedDocId);
  selectedDocIdRef.current = selectedDocId;
  const [summarizingDocId, setSummarizingDocId] = useState<string | null>(null);
  const [summarizingAll, setSummarizingAll] = useState(false);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);
  // Author for audit events (finalized summary, AI document summary).
  const authUser = useAuthStore((state) => state.user);
  const authorName =
    (authUser as any)?.name ||
    `${(authUser as any)?.firstName || ''} ${(authUser as any)?.lastName || ''}`.trim() ||
    (authUser as any)?.email ||
    'Clinician';

  // Enriched contact info pulled from the external `levitanai_contacts` DB
  // (MRN, insurance, address, emergency contact, fee/payment, last visit,
  // archived/deceased, tags). `enrichedStatus` distinguishes the three
  // degradation modes so we can show a subtle caption ONLY when the
  // external DB is actually unreachable — silent on missing-row.
  const [enriched, setEnriched] = useState<any | null>(null);
  const [enrichedStatus, setEnrichedStatus] = useState<
    'idle' | 'ok' | 'not_found' | 'unreachable' | 'not_configured'
  >('idle');
  // The "Patient Corpus" — historical clinical narrative pulled from the
  // external DB alongside the structured profile. Notes and comms are
  // folded into the unified Timeline; documents into the Documents panel.
  // This is the substrate a future per-patient LLM chat will read from.
  const [externalNotes, setExternalNotes] = useState<any[]>([]);
  const [externalDocs, setExternalDocs] = useState<any[]>([]);
  const [externalComms, setExternalComms] = useState<any[]>([]);

  const [historyEvents, setHistoryEvents] = useState<HistoryEvent[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<string>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historyDatePreset, setHistoryDatePreset] = useState<
    'all' | '30d' | '90d' | 'ytd' | 'since_visit' | 'custom'
  >('all');
  // Custom range bounds (yyyy-mm-dd strings from native <input type="date">).
  // Only applied when historyDatePreset === 'custom'. Either end may be blank
  // — blank start means "from the beginning", blank end means "until now".
  const [historyCustomStart, setHistoryCustomStart] = useState('');
  const [historyCustomEnd, setHistoryCustomEnd] = useState('');
  // How many timeline entries to render at once. Defaults to the most
  // recent HISTORY_PAGE_SIZE; "Show more" grows it by a page and "Show all"
  // lifts the cap entirely. Reset to the default whenever the active
  // filter/search/date-range changes (see the effect below) so the user
  // always lands on the most recent slice after re-scoping.
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);
  // Whole-section collapse for the History panel. Expanded by default.

  // Shared per-user collapse state for the enriched clinical header strip and
  // the three info cards below it. Owned here so a single "Expand/Collapse all"
  // control and the individual section toggles all operate on one store.
  const sectionCollapse = usePatientSectionCollapse();

  // Most recent past calendar event ("last visit") used by the
  // "Since last visit" preset. null when the patient has no past visits.
  const lastVisitDate = useMemo<Date | null>(() => {
    const now = Date.now();
    let latest: Date | null = null;
    const consider = (ts?: string | null) => {
      if (!ts) return;
      const d = new Date(ts);
      if (Number.isNaN(d.getTime()) || d.getTime() > now) return;
      if (!latest || d.getTime() > latest.getTime()) latest = d;
    };
    // Anchor on the most recent *documented* encounter (the real visits). These
    // are merged into the timeline client-side, so read them from the raw
    // `encounters` list rather than the backend history events.
    for (const e of encounters || []) consider(e.encounter_date);
    // Fall back to a past calendar appointment once scheduling data exists.
    for (const e of historyEvents) {
      if ((e.event_source || '') === 'calendar') consider(e.event_date || e.created_at);
    }
    return latest;
  }, [encounters, historyEvents]);

  // Lower bound Date for the active date preset, or null for "all". Events
  // on or after this Date are kept. 'since_visit' falls back to 'all' when
  // there is no past visit to anchor against. 'custom' uses the start input
  // (blank = no lower bound).
  const dateCutoff = useMemo<Date | null>(() => {
    const now = Date.now();
    switch (historyDatePreset) {
      case '30d':
        return new Date(now - 30 * 24 * 60 * 60 * 1000);
      case '90d':
        return new Date(now - 90 * 24 * 60 * 60 * 1000);
      case 'ytd':
        return new Date(new Date().getFullYear(), 0, 1);
      case 'since_visit':
        return lastVisitDate;
      case 'custom': {
        if (!historyCustomStart) return null;
        const d = new Date(historyCustomStart + 'T00:00:00');
        return Number.isNaN(d.getTime()) ? null : d;
      }
      case 'all':
      default:
        return null;
    }
  }, [historyDatePreset, lastVisitDate, historyCustomStart]);

  // Upper bound for the custom preset only; inclusive of the chosen end day.
  const dateCutoffEnd = useMemo<Date | null>(() => {
    if (historyDatePreset !== 'custom' || !historyCustomEnd) return null;
    // End of selected day (23:59:59.999) so the picked day itself is included.
    const d = new Date(historyCustomEnd + 'T23:59:59.999');
    return Number.isNaN(d.getTime()) ? null : d;
  }, [historyDatePreset, historyCustomEnd]);

  // Folds external clinician notes + legacy communications (pulled from
  // `levitanai_contacts`) into the same timeline shape as the local events
  // so date filters, search, and source chips work on them uniformly.
  // Notes get their own 'note' source (dedicated "Notes" chip — a doctor who
  // composed a Note in the Messages tab looks for "Notes", not "Chart
  // Edits"); comms show as 'spruce' (legacy messaging continues that thread).
  const allHistoryEvents = useMemo<HistoryEvent[]>(() => {
    const merged: HistoryEvent[] = [...historyEvents];
    for (const n of externalNotes) {
      const body = String(n.content || '');
      merged.push({
        id: `ext_note_${n.id}`,
        event_type: 'note',
        event_source: 'note',
        event_category: `external_note${n.is_pinned ? '_pinned' : ''}`,
        event_title: n.is_pinned ? '📌 Note' : 'Note',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: n.author || null,
        event_date: n.created_at || undefined,
        created_at: n.created_at || undefined,
        updated_at: n.updated_at || undefined,
        metadata: { source: 'levitanai_contacts', noteType: n.note_type, isPinned: !!n.is_pinned },
      });
    }
    for (const c of externalComms) {
      const subject = c.subject || c.conversation_title || '';
      const body = String(c.content || '');
      const kind = (c.communication_type || 'message').toUpperCase();
      const dir = c.direction === 'outbound' ? '→' : '←';
      merged.push({
        id: `ext_comm_${c.id}`,
        event_type: 'message',
        event_source: 'spruce',
        event_category: 'external_communication',
        event_title: `${kind} ${dir} ${subject || (body.split('\n')[0] || '').slice(0, 80)}`.trim(),
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: null,
        event_date: c.sent_at || c.created_at || c.last_message_at || undefined,
        created_at: c.created_at || undefined,
        metadata: {
          source: 'levitanai_contacts',
          communicationType: c.communication_type,
          direction: c.direction,
          messageCount: c.message_count,
        },
      });
    }
    // Encounters (visit notes) as their own timeline category.
    for (const e of encounters) {
      const body = [e.assessment, e.plan].filter(Boolean).join('\n').trim()
        || [e.subjective, e.objective].filter(Boolean).join('\n').trim()
        || e.chief_complaint || '';
      const title = [e.encounter_type || 'Visit', e.chief_complaint]
        .filter(Boolean).join(' — ');
      merged.push({
        id: `encounter_${e.id}`,
        event_type: 'encounter',
        event_source: 'encounter',
        event_category: 'encounter',
        event_title: title || 'Visit note',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: e.provider || null,
        event_date: e.encounter_date || undefined,
        created_at: e.encounter_date || undefined,
        metadata: { source: 'encounter', isVirtual: (e as any).is_virtual },
      });
    }
    // Documents as their own timeline category — local uploads AND external
    // `contact_documents` (previously only local docs got timeline events,
    // so externally-synced documents were invisible under the Documents chip).
    for (const d of documents) {
      const body = String(d.summary_text || '');
      merged.push({
        id: `document_${d.id}`,
        event_type: 'document',
        event_source: 'document',
        event_category: 'document',
        event_title: d.filename || 'Document',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: d.uploaded_by || null,
        event_date: d.created_at || d.createdAt || undefined,
        created_at: d.created_at || d.createdAt || undefined,
        metadata: { source: 'document', status: d.status },
      });
    }
    for (const d of externalDocs) {
      const body = String(d.description || '');
      merged.push({
        id: `document_ext_${d.id}`,
        event_type: 'document',
        event_source: 'document',
        event_category: 'document',
        event_title: d.original_filename || d.filename || 'Document',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: d.uploaded_by || null,
        event_date: d.created_at || undefined,
        created_at: d.created_at || undefined,
        metadata: { source: 'external_document', documentType: d.document_type },
      });
    }
    // Diagnoses as their own timeline category (anchored on onset).
    for (const dx of clinicalDiagnoses) {
      const status = (dx.status || '').trim();
      const tail = [dx.icd10_code, dx.acuity, status].filter(Boolean).join(' · ');
      const body = [dx.comment, tail].filter(Boolean).join('\n').trim();
      merged.push({
        id: `diagnosis_${dx.id}`,
        event_type: 'diagnosis',
        event_source: 'diagnosis',
        event_category: 'diagnosis',
        event_title: dx.description || 'Diagnosis',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: dx.created_by || null,
        event_date: dx.onset_date || dx.created_at || undefined,
        created_at: dx.created_at || undefined,
        metadata: { source: 'diagnosis', status: dx.status, icd10: dx.icd10_code },
      });
    }
    // Confirmed Granola meeting notes.
    for (const n of granolaNotes) {
      const body = String(n.summary_text || '');
      merged.push({
        id: `granola_${n.id}`,
        event_type: 'granola',
        event_source: 'granola',
        event_category: 'granola',
        event_title: n.title || 'Granola note',
        event_body: body.length > 240 ? `${body.slice(0, 240)}…` : body,
        event_body_full: body,
        author: null,
        event_date: n.note_created_at || undefined,
        created_at: n.note_created_at || undefined,
        metadata: { source: 'granola', webUrl: n.web_url },
      });
    }
    // Local `historyEvents` is delivered pre-sorted by the backend, but
    // once we append external rows we need a single descending sort over
    // the union so a 2025 external note shows above a 2024 local event.
    merged.sort((a, b) => {
      const at = new Date(a.event_date || a.created_at || 0).getTime();
      const bt = new Date(b.event_date || b.created_at || 0).getTime();
      return (Number.isFinite(bt) ? bt : 0) - (Number.isFinite(at) ? at : 0);
    });
    return merged;
  }, [historyEvents, externalNotes, externalComms, encounters, documents, externalDocs, clinicalDiagnoses, granolaNotes]);

  // Events after date + search filtering — source chips count off this so
  // their numbers always match what the user is currently looking at.
  const scopedHistoryEvents = useMemo(() => {
    const needle = historySearch.trim().toLowerCase();
    return allHistoryEvents.filter((e) => {
      if (dateCutoff || dateCutoffEnd) {
        const ts = e.event_date || e.created_at;
        if (!ts) return false;
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return false;
        if (dateCutoff && d.getTime() < dateCutoff.getTime()) return false;
        if (dateCutoffEnd && d.getTime() > dateCutoffEnd.getTime()) return false;
      }
      if (needle) {
        const haystack = [
          e.event_title || '',
          e.event_body || '',
          e.event_body_full || '',
          e.author || '',
          e.event_type || '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [allHistoryEvents, dateCutoff, dateCutoffEnd, historySearch]);

  const filteredHistoryEvents = useMemo(
    () =>
      historyFilter === 'all'
        // AI-only markers are opt-in: hidden from the default "All" view so the
        // clinical record stays clean unless the doctor selects that category.
        ? scopedHistoryEvents.filter((e) => !AI_ONLY_KEYS.has(e.event_source || ''))
        : scopedHistoryEvents.filter((e) => (e.event_source || 'unknown') === historyFilter),
    [scopedHistoryEvents, historyFilter],
  );
  // Whenever the user re-scopes the timeline (source filter, free-text
  // search, or any part of the date-range selection) snap back to the
  // first page so they always see the most recent matching entries first.
  useEffect(() => {
    setHistoryLimit(HISTORY_PAGE_SIZE);
  }, [historyFilter, historySearch, historyDatePreset, historyCustomStart, historyCustomEnd, spruceEntityId]);
  const visibleHistoryEvents = useMemo(
    () => filteredHistoryEvents.slice(0, historyLimit),
    [filteredHistoryEvents, historyLimit],
  );
  const historyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of scopedHistoryEvents) {
      const src = e.event_source || 'unknown';
      counts[src] = (counts[src] || 0) + 1;
    }
    return counts;
  }, [scopedHistoryEvents]);
  // The default "All" view excludes AI-only markers, so its count must too.
  const nonAiHistoryCount = useMemo(
    () => scopedHistoryEvents.filter((e) => !AI_ONLY_KEYS.has(e.event_source || '')).length,
    [scopedHistoryEvents],
  );

  const [loadingPatient, setLoadingPatient] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [recordUploading, setRecordUploading] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteDate, setPasteDate] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordTitle, setRecordTitle] = useState('');
  const [recordDate, setRecordDate] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [recordError, setRecordError] = useState('');

  const [summaryDraft, setSummaryDraft] = useState('');
  const [chartDraft, setChartDraft] = useState('');
  const [localNormalOverride, setLocalNormalOverride] = useState<string | null>(null);
  const [localChartOverride, setLocalChartOverride] = useState<string | null>(null);
  const persistSummaryRef = useRef<{
    normal: { pending: string; lastSaved?: string; flushing: boolean };
    chart: { pending: string; lastSaved?: string; flushing: boolean };
  }>({
    normal: { pending: '', flushing: false },
    chart: { pending: '', flushing: false },
  });
  const [summaryView, setSummaryView] = useState<'normal' | 'chart'>(() => {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      const userId = user?.id || user?.email || 'default';
      const stored = localStorage.getItem(`patientDetails_summaryView_${userId}`);
      return stored === 'chart' ? 'chart' : 'normal';
    } catch (err) {
      return 'normal';
    }
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordChunksRef = useRef<BlobPart[]>([]);

  const latestDetails = useMemo(() => {
    const map: Record<string, PatientDetail> = {};
    details.forEach((detail) => {
      const category = detail.category || '';
      if (!category) return;
      const createdAt = detail.createdAt || detail.created_at || '';
      if (!map[category] || (createdAt && createdAt > (map[category].createdAt || map[category].created_at || ''))) {
        map[category] = detail;
      }
    });
    return map;
  }, [details]);

  const serverNormalOverride = latestDetails['summary_override'];
  const serverChartOverride = latestDetails['summary_chart_override'];
  const hasServerNormalOverride = Boolean(serverNormalOverride);
  const hasServerChartOverride = Boolean(serverChartOverride);
  const hasManualSummary = localNormalOverride !== null || hasServerNormalOverride;
  const hasManualChartSummary = localChartOverride !== null || hasServerChartOverride;



  // Folds external `contact_documents` (from levitanai_contacts) into the
  // same documents list the panel already renders. External rows get a
  // synthetic `ext_doc_<uuid>` id and a `source='external'` flag so the
  // View/Download buttons can hit the external download_url directly
  // instead of the local /documents/:id/download endpoint.
  const mergedDocuments = useMemo(() => {
    const ext = externalDocs.map((d) => ({
      id: `ext_doc_${d.id}`,
      filename: d.original_filename || d.filename || 'Document',
      content_type: d.mime_type || null,
      file_size: d.file_size != null ? Number(d.file_size) : undefined,
      status: d.document_type || 'document',
      created_at: d.created_at || undefined,
      summary_text: d.description || '',
      uploaded_by: d.uploaded_by || null,
      uploaded_by_user_id: null,
      // Extra fields used by the render branch below.
      _source: 'external' as const,
      _downloadUrl: d.download_url || null,
      _linkExpiresAt: d.link_expires_at || null,
    }));
    return [...documents, ...ext];
  }, [documents, externalDocs]);

  const selectedDoc = useMemo(
    () => mergedDocuments.find((doc) => doc.id === selectedDocId) || null,
    [mergedDocuments, selectedDocId],
  );
  // External documents come with pre-signed URLs that can expire. If the
  // service has stamped `link_expires_at` in the past we treat the link as
  // dead (the upstream would 403 anyway) so the user sees a disabled
  // button + caption instead of clicking through to an error page.
  const externalLinkExpired = useMemo(() => {
    const sd: any = selectedDoc;
    if (!sd || sd._source !== 'external') return false;
    const exp = sd._linkExpiresAt;
    if (!exp) return false;
    const t = new Date(exp).getTime();
    return Number.isFinite(t) && t < Date.now();
  }, [selectedDoc]);
  // Prefer a real patient name over the base chart record's placeholder
  // ("Patient entity_..."). Order: contacts-master name (enriched) → the name
  // passed in via the link/contact → a non-placeholder base record name → the
  // raw prop → "Patient". The Spruce entity id is shown separately as subtext.
  const isPlaceholderName = (n?: string) => {
    const s = (n || '').trim();
    return !s || /^patient$/i.test(s) || (/^patient\b/i.test(s) && /entity_/i.test(s));
  };
  const externalName =
    (enriched?.fullName || '').trim() ||
    [enriched?.firstName, enriched?.lastName].filter(Boolean).join(' ').trim();
  const displayName =
    externalName ||
    (patientName && !isPlaceholderName(patientName) ? patientName.trim() : '') ||
    (!isPlaceholderName(patient?.patientName) ? (patient?.patientName || '').trim() : '') ||
    patientName ||
    'Patient';
  const entitySubId = patient?.patientId || spruceEntityId || '';
  // DOB/gender: the chart record (patient) often lacks these; the contacts-master
  // record (enriched, from levitanai_contacts) is the authoritative demographic
  // source, so fall back to it before the route-supplied prop.
  const displayDob = patient?.dateOfBirth || enriched?.dateOfBirth || patientDob || '';
  const displayGender = patient?.gender || enriched?.gender || patientGender || 'Unknown';
  const displayPhone = patient?.patientPhone || patientPhone || '—';
  const displayEmail = patient?.patientEmail || patientEmail || '—';

  const parseDateOnly = (value?: string) => {
    if (!value) return null;
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const age = useMemo(() => {
    if (!displayDob) return null;
    const dobDate = parseDateOnly(displayDob);
    if (!dobDate) return null;
    try {
      return differenceInYears(new Date(), dobDate);
    } catch (err) {
      return null;
    }
  }, [displayDob]);


  const formattedDob = useMemo(() => {
    if (!displayDob) return '—';
    const dobDate = parseDateOnly(displayDob);
    if (!dobDate) return displayDob;
    try {
      return formatDateOnly(dobDate, dateTimeFormat) || displayDob;
    } catch (err) {
      return displayDob;
    }
  }, [displayDob, dateTimeFormat]);

  const formatEventDate = (value?: string) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return formatDateOnly(value, dateTimeFormat) || value;
      const datePart = formatDateOnly(value, dateTimeFormat) || d.toLocaleDateString();
      const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${datePart} · ${timePart}`;
    } catch (err) {
      return value;
    }
  };

  // Unit system preference (shared with PatientClinicalStrip via localStorage).
  // Drives display-only conversion of vital chart edits in the timeline so
  // the units shown here match the chips on the clinical strip.
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>(() => {
    try {
      return (localStorage.getItem('vitalsUnitSystem') as 'metric' | 'imperial') === 'imperial'
        ? 'imperial'
        : 'metric';
    } catch {
      return 'metric';
    }
  });
  // Pick up changes the user makes in the clinical strip without a reload.
  useEffect(() => {
    const sync = () => {
      try {
        setUnitSystem(
          (localStorage.getItem('vitalsUnitSystem') as 'metric' | 'imperial') === 'imperial'
            ? 'imperial'
            : 'metric',
        );
      } catch { /* noop */ }
    };
    window.addEventListener('storage', sync);
    window.addEventListener('vitalsUnitSystemChange', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('vitalsUnitSystemChange', sync);
    };
  }, [setUnitSystem]);

  // Convert a stored vital chart-edit value (metric in storage) into the
  // user's preferred display units. Returns the value unchanged for
  // non-vital categories or when the body isn't a parseable number.
  const formatVitalBody = (category: string | undefined, body: string): string => {
    if (!category || !body) return body;
    const n = parseFloat(body);
    if (!Number.isFinite(n)) return body; // e.g. "120/80" for BP, "non-smoker" — pass through
    if (category === 'vitals_height') {
      if (unitSystem === 'metric') return `${n} cm`;
      const totalIn = n / 2.54;
      const ft = Math.floor(totalIn / 12);
      const inch = Math.round(totalIn - ft * 12);
      return `${ft}′ ${inch}″`;
    }
    if (category === 'vitals_weight') {
      if (unitSystem === 'metric') return `${n} kg`;
      return `${(n * 2.20462).toFixed(1)} lb`;
    }
    if (category === 'vitals_temp') {
      if (unitSystem === 'metric') return `${n.toFixed(1)} °C`;
      return `${((n * 9) / 5 + 32).toFixed(1)} °F`;
    }
    return body;
  };

  const loadPatient = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoadingPatient(true);
    try {
      const response = await patientDetailsApi.getPatient(spruceEntityId);
      if (response.data?.success && response.data?.data) {
        setPatient(response.data.data);
        setDetails(response.data.data.details || []);
      }
    } catch (err) {
      setError('Failed to load patient details.');
    } finally {
      setLoadingPatient(false);
    }
  }, [spruceEntityId]);

  // Practice Fusion fallback: mirror the chip strip's lookup so the Clinical
  // Snapshot surfaces imported PF data (allergies / meds / dx / family history)
  // even when nothing has been entered manually. Uses the route/contact props
  // first, then falls back to the enriched contacts-master record so it works
  // in the full-page (triage popup) view too. Fallback-only — manual chart
  // entries always win (the merge below skips any category already present).
  useEffect(() => {
    let cancelled = false;
    const name =
      patientName ||
      (enriched?.fullName || '').trim() ||
      [enriched?.firstName, enriched?.lastName].filter(Boolean).join(' ').trim() ||
      undefined;
    const phone = patientPhone || enriched?.phone || enriched?.phoneFormatted || undefined;
    const email = patientEmail || enriched?.email || undefined;
    if (!spruceEntityId || (!name && !phone && !email)) {
      setPfDetails([]);
      return;
    }
    (async () => {
      try {
        const pfRes = await practiceFusionApi.lookup({ name, phone, email });
        if (cancelled) return;
        const pf = pfRes.data?.success ? pfRes.data?.data : null;
        if (!pf) {
          setPfDetails([]);
          return;
        }
        const entries: PatientDetail[] = [];
        const PF = 'practice_fusion';
        if (pf.allergies) entries.push({ category: 'allergies', content: pf.allergies, source: PF });
        if (pf.currentMedications)
          entries.push({ category: 'current_medications', content: pf.currentMedications, source: PF });
        if (pf.primaryDiagnosis)
          entries.push({ category: 'primary_diagnosis', content: pf.primaryDiagnosis, source: PF });
        if (pf.familyHistory)
          entries.push({ category: 'family_history', content: pf.familyHistory, source: PF });
        setPfDetails(entries);
      } catch {
        if (!cancelled) setPfDetails([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spruceEntityId, patientName, patientPhone, patientEmail, enriched]);

  // Snapshot input = manual chart entries + PF fallback for any category that
  // has no manual entry. Keeps `details` (history/timeline) untouched.
  const snapshotDetails = useMemo(() => {
    if (!pfDetails.length) return details;
    const existing = new Set((details || []).map((d) => d.category).filter(Boolean));
    const fallback = pfDetails.filter((d) => d.category && !existing.has(d.category));
    return fallback.length ? [...details, ...fallback] : details;
  }, [details, pfDetails]);

  const loadSummary = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoadingSummary(true);
    try {
      const response = await patientDetailsApi.getSummary(spruceEntityId);
      if (response.data?.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      setError('Failed to load summaries.');
    } finally {
      setLoadingSummary(false);
    }
  }, [spruceEntityId]);

  const loadDocuments = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoadingDocuments(true);
    try {
      const response = await patientDetailsApi.getDocuments(spruceEntityId);
      if (response.data?.success) {
        const docs = response.data.data || [];
        setDocuments(docs);
        if (docs.length) {
          const prefer = initialDocId && docs.some((d: any) => String(d.id) === String(initialDocId))
            ? String(initialDocId)
            : selectedDocIdRef.current;
          if (prefer && docs.some((d: any) => String(d.id) === String(prefer))) {
            setSelectedDocId(String(prefer));
          } else {
            setSelectedDocId(docs[0].id);
          }
        }
      }
    } catch (err) {
      setError('Failed to load documents.');
    } finally {
      setLoadingDocuments(false);
    }
  }, [spruceEntityId, initialDocId]);

  // On-demand: extract + AI-summarize a single document (cached after first run).
  const handleSummarizeDocument = useCallback(async (docId: string, force = false) => {
    if (!spruceEntityId || !docId) return;
    setSummarizingDocId(docId);
    try {
      const response = await patientDetailsApi.summarizeDocument(spruceEntityId, docId, force);
      const summary = response.data?.data?.summary;
      if (summary) {
        setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, summary_text: summary } : d)));
        // Audit: record that this document was AI-summarized, in the timeline.
        const filename = documents.find((d) => d.id === docId)?.filename || 'document';
        await patientDetailsApi
          .addDetail(spruceEntityId, 'ai_document_summary', `AI-summarized "${filename}"`, authorName)
          .catch(() => {});
        window.dispatchEvent(new CustomEvent('patientChartChange', { detail: { spruceEntityId } }));
      }
      await loadDocuments();
    } catch (err) {
      setError('Failed to summarize document.');
    } finally {
      setSummarizingDocId(null);
    }
  }, [spruceEntityId, loadDocuments, documents, authorName]);

  // On-demand: extract + AI-summarize ALL not-yet-cached docs (runs in background).
  const handleSummarizeAllDocuments = useCallback(async () => {
    if (!spruceEntityId) return;
    setSummarizingAll(true);
    try {
      await patientDetailsApi.summarizeAllDocuments(spruceEntityId);
      // Audit: one aggregate timeline entry for the bulk action (per-doc events
      // would be noisy).
      await patientDetailsApi
        .addDetail(spruceEntityId, 'ai_document_summary', 'AI-summarized all documents', authorName)
        .catch(() => {});
      window.dispatchEvent(new CustomEvent('patientChartChange', { detail: { spruceEntityId } }));
      const refresh = (n: number) => {
        if (n <= 0) { setSummarizingAll(false); return; }
        setTimeout(async () => { await loadDocuments(); refresh(n - 1); }, 8000);
      };
      refresh(6);
    } catch (err) {
      setError('Failed to start document summarization.');
      setSummarizingAll(false);
    }
  }, [spruceEntityId, loadDocuments, authorName]);

  const loadHistory = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoadingHistory(true);
    try {
      const response = await patientDetailsApi.getHistory(spruceEntityId, { includeConsultations: true });
      if (response.data?.success) {
        const data = response.data.data || {};
        setHistoryEvents(data.events || []);
      }
    } catch (err) {
      setError('Failed to load history.');
    } finally {
      setLoadingHistory(false);
    }
  }, [spruceEntityId]);

  const syncDrafts = useCallback(() => {
    if (localNormalOverride === null) {
      const summaryText = hasServerNormalOverride
        ? (serverNormalOverride?.content ?? '')
        : (summary?.current?.summary_text || '');
      setSummaryDraft(summaryText);
    }
    if (localChartOverride === null) {
      const chartText = hasServerChartOverride
        ? (serverChartOverride?.content ?? '')
        : (summary?.current?.summary_chart_text || '');
      setChartDraft(chartText);
    }
  }, [
    localNormalOverride,
    localChartOverride,
    hasServerNormalOverride,
    hasServerChartOverride,
    serverNormalOverride,
    serverChartOverride,
    summary,
  ]);

  // Fetches the external `levitanai_contacts` enrichment payload (MRN,
  // insurance, address, fee status, etc.) alongside the existing patient
  // details call. Treated as best-effort — a network failure flips the
  // small inline caption but never blocks the rest of the page.
  const loadEnriched = useCallback(async () => {
    if (!spruceEntityId) {
      setEnriched(null); setEnrichedStatus('idle');
      setExternalNotes([]); setExternalDocs([]); setExternalComms([]);
      return;
    }
    try {
      const response = await patientDetailsApi.getEnriched(spruceEntityId);
      if (response.data?.success) {
        const data = response.data.data || {};
        const ext = data.external || null;
        setEnriched(ext);
        setEnrichedStatus(data.externalStatus || (ext ? 'ok' : 'not_found'));
        setExternalNotes(Array.isArray(ext?.notes) ? ext.notes : []);
        setExternalDocs(Array.isArray(ext?.documents) ? ext.documents : []);
        setExternalComms(Array.isArray(ext?.communications) ? ext.communications : []);
      } else {
        setEnriched(null); setEnrichedStatus('unreachable');
        setExternalNotes([]); setExternalDocs([]); setExternalComms([]);
      }
    } catch {
      setEnriched(null); setEnrichedStatus('unreachable');
      setExternalNotes([]); setExternalDocs([]); setExternalComms([]);
    }
  }, [spruceEntityId]);

  useEffect(() => {
    setError(null);
    loadPatient();
    loadSummary();
    loadDocuments();
    loadHistory();
    loadEnriched();
  }, [spruceEntityId, loadPatient, loadSummary, loadDocuments, loadHistory, loadEnriched]);

  // Live-refresh the timeline (and the patient summary chips) whenever
  // the clinical strip emits a chart-change event after a successful
  // save — vitals, allergies, primary dx, current meds, family history.
  // Matches the user expectation that an edit shows up in the History
  // panel immediately without a page refresh. Scoped to the current
  // patient so background tabs don't refetch unnecessarily.
  useEffect(() => {
    if (!spruceEntityId) return;
    const onChartChange = (e: Event) => {
      const detail = (e as CustomEvent<{ spruceEntityId?: string }>).detail;
      if (detail?.spruceEntityId && detail.spruceEntityId !== spruceEntityId) return;
      loadHistory();
      loadPatient();
    };
    window.addEventListener('patientChartChange', onChartChange);
    return () => window.removeEventListener('patientChartChange', onChartChange);
  }, [spruceEntityId, loadHistory, loadPatient]);

  useEffect(() => {
    syncDrafts();
  }, [latestDetails, summary, syncDrafts]);

  useEffect(() => {
    try {
      const userRaw = localStorage.getItem('user');
      const user = userRaw ? JSON.parse(userRaw) : null;
      const userId = user?.id || user?.email || 'default';
      localStorage.setItem(`patientDetails_summaryView_${userId}`, summaryView);
    } catch (err) {
      // ignore storage errors
    }
  }, [summaryView]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!spruceEntityId || !event.target.files || !event.target.files[0]) return;
    const file = event.target.files[0];
    setUploading(true);
    setError(null);
    try {
      const uploadRes = await patientDetailsApi.uploadDocument(spruceEntityId, file, {
        patientName: displayName,
        patientPhone: displayPhone,
        patientEmail: displayEmail,
        dateOfBirth: displayDob,
        gender: displayGender,
      });
      const createdDoc = uploadRes.data?.data?.document || uploadRes.data?.data;
      if (createdDoc?.id) {
        setDocuments((prev) =>
          prev.some((d) => String(d.id) === String(createdDoc.id)) ? prev : [createdDoc, ...prev]
        );
        setSelectedDocId(String(createdDoc.id));
      }
      window.dispatchEvent(new CustomEvent('patientChartChange', { detail: { spruceEntityId } }));
      await Promise.all([loadDocuments(), loadSummary(), loadPatient()]);
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenPaste = () => {
    setPasteError('');
    setPasteOpen(true);
  };

  const handleClosePaste = () => {
    if (pasting) return;
    setPasteOpen(false);
    setPasteTitle('');
    setPasteDate('');
    setPasteText('');
    setPasteError('');
  };

  const handleSubmitPaste = async () => {
    if (!spruceEntityId) return;
    const trimmedText = pasteText.trim();
    if (!trimmedText) {
      setPasteError('Please paste some text to summarize.');
      return;
    }
    setPasting(true);
    setError(null);
    try {
      const pasteRes = await patientDetailsApi.uploadText(spruceEntityId, {
        text: trimmedText,
        title: pasteTitle.trim() || undefined,
        documentDate: pasteDate.trim() || undefined,
        patientName: displayName,
        patientPhone: displayPhone,
        patientEmail: displayEmail,
        dateOfBirth: displayDob,
        gender: displayGender,
        createdBy: 'manual',
      });
      const createdDoc = pasteRes.data?.data?.document || pasteRes.data?.data;
      if (createdDoc?.id) {
        setDocuments((prev) =>
          prev.some((d) => String(d.id) === String(createdDoc.id)) ? prev : [createdDoc, ...prev]
        );
        setSelectedDocId(String(createdDoc.id));
      }
      window.dispatchEvent(new CustomEvent('patientChartChange', { detail: { spruceEntityId } }));
      await Promise.all([loadDocuments(), loadSummary(), loadPatient()]);
      handleClosePaste();
    } catch (err) {
      setError('Paste text failed. Please try again.');
    } finally {
      setPasting(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainder.toString().padStart(2, '0')}`;
  };

  const stopMediaStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const handleOpenRecord = () => {
    setRecordError('');
    setRecordOpen(true);
  };

  const handleCloseRecord = () => {
    if (recording) return;
    setRecordOpen(false);
    setRecordTitle('');
    setRecordDate('');
    setRecordedBlob(null);
    setRecordSeconds(0);
    recordChunksRef.current = [];
    setRecordError('');
  };

  const handleStartRecording = async () => {
    setRecordError('');
    setRecordedBlob(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecordError('Audio recording is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const preferredType = 'audio/webm;codecs=opus';
      const mimeType = MediaRecorder.isTypeSupported(preferredType) ? preferredType : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recordChunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(recordChunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        recordChunksRef.current = [];
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordSeconds(0);
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
      }
      recordTimerRef.current = setInterval(() => {
        setRecordSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORD_SECONDS) {
            setRecordError('Reached 60-minute limit. Recording stopped.');
            handleStopRecording();
            return MAX_RECORD_SECONDS;
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setRecordError('Microphone access denied or unavailable.');
      stopMediaStream();
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    stopMediaStream();
  };

  const handleUploadRecording = async () => {
    if (!spruceEntityId || !recordedBlob) return;
    setRecordUploading(true);
    setError(null);
    try {
      const safeTitle = recordTitle.trim();
      const dateSuffix = recordDate ? `-${recordDate}` : '';
      const filename = safeTitle ? `${safeTitle}${dateSuffix}.webm` : `Recording${dateSuffix}.webm`;
      const file = new File([recordedBlob], filename, { type: recordedBlob.type || 'audio/webm' });
      const recordRes = await patientDetailsApi.uploadDocument(spruceEntityId, file, {
        patientName: displayName,
        patientPhone: displayPhone,
        patientEmail: displayEmail,
        dateOfBirth: displayDob,
        gender: displayGender,
        documentDate: recordDate || undefined,
      });
      const createdDoc = recordRes.data?.data?.document || recordRes.data?.data;
      if (createdDoc?.id) {
        setDocuments((prev) =>
          prev.some((d) => String(d.id) === String(createdDoc.id)) ? prev : [createdDoc, ...prev]
        );
        setSelectedDocId(String(createdDoc.id));
      }
      window.dispatchEvent(new CustomEvent('patientChartChange', { detail: { spruceEntityId } }));
      await Promise.all([loadDocuments(), loadSummary(), loadPatient()]);
      handleCloseRecord();
    } catch (err) {
      setError('Audio upload failed. Please try again.');
    } finally {
      setRecordUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
      }
      stopMediaStream();
    };
  }, []);

  const persistSummary = useCallback(async (which: 'normal' | 'chart', value: string) => {
    if (!spruceEntityId) return;
    const category = which === 'normal' ? 'summary_override' : 'summary_chart_override';
    const slot = persistSummaryRef.current[which];
    slot.pending = value;
    if (slot.flushing) return;
    slot.flushing = true;
    setSavingSummary(true);
    try {
      while (slot.pending !== slot.lastSaved) {
        const toSave = slot.pending;
        await patientDetailsApi.addDetail(spruceEntityId, category, toSave, authorName);
        slot.lastSaved = toSave;
      }
    } catch (err) {
      setError('Failed to save summary override.');
    } finally {
      slot.flushing = false;
      setSavingSummary(false);
    }
  }, [spruceEntityId, authorName]);

  useEffect(() => {
    setLocalNormalOverride(null);
    setLocalChartOverride(null);
    persistSummaryRef.current = {
      normal: { pending: '', flushing: false },
      chart: { pending: '', flushing: false },
    };
  }, [spruceEntityId]);

  if (!spruceEntityId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Select a patient to view details.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {(view === 'details' || view === 'all') && (
      <>
      <Paper sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          {showBackButton && (
            <Button variant="outlined" size={isMobile ? 'small' : 'medium'} startIcon={<ArrowBackIcon />} onClick={onBack}>
              {backLabel}
            </Button>
          )}
          {/* Identity (name / DOB / phone / email) lives ONLY in the patient
              header above on every breakpoint — desktop header now carries
              DOB, and its icon actions cover call/email. This card opens
              straight at status chips + Additional Info; DOB and the Spruce
              entity id surface in the Additional Info grid. */}
          <Box sx={{
            display: 'flex',
            gap: 0.5,
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 0,
          }}>
            {enriched?.isDeceased && (
              <Chip label="Deceased" color="error" size="small" />
            )}
            {enriched?.isArchived && !enriched?.isDeceased && (
              <Chip label="Archived" color="warning" variant="outlined" size="small" />
            )}
          </Box>
        </Box>
        {(() => {
          // Consolidated "Additional Info" — the single home for contact /
          // address / administrative metadata (the duplicate cards that used
          // to sit at the bottom of the page now fold in here). Prefer the
          // enriched contacts-master record, falling back to the raw contact
          // so the section stays populated on every mount.
          const info: any = enriched || contact;
          if (!info) {
            // Only show the caption when the external DB is genuinely
            // unreachable. Silent for 'not_found' (no row for this patient)
            // and 'not_configured' (feature disabled in this environment)
            // so we don't nag staff with irrelevant warnings.
            if (enrichedStatus === 'unreachable') {
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled', fontStyle: 'italic' }}>
                  Couldn't load extra patient info.
                </Typography>
              );
            }
            return null;
          }
          // Each item can optionally span more than one grid cell at
          // larger breakpoints — fields like Address and Emergency contact
          // are usually multi-line and look much better with the extra
          // horizontal room. `wide` = 2 cells on sm+, 'full' = the whole
          // row on every breakpoint (rare; reserved for tags).
          const items: Array<{ label: string; value: React.ReactNode; span?: 'wide' | 'full' }> = [];
          // Identity card is gone on every breakpoint — DOB, phone and email
          // surface here (headers show icons/age only).
          if (formattedDob) items.push({ label: 'DOB', value: `${formattedDob}${age !== null ? ` · ${age} yrs` : ''}` });
          if (displayPhone !== '—') {
            items.push({
              label: 'Phone',
              value: (
                <Typography component="a" href={`tel:${displayPhone.replace(/[^+\d]/g, '')}`} sx={{ fontSize: 'inherit', color: 'primary.main', textDecoration: 'none' }}>
                  {displayPhone}
                </Typography>
              ),
            });
          }
          if (displayEmail !== '—') {
            items.push({
              label: 'Email',
              value: (
                <Typography component="a" href={`mailto:${displayEmail}`} sx={{ fontSize: 'inherit', color: 'primary.main', textDecoration: 'none', wordBreak: 'break-all' }}>
                  {displayEmail}
                </Typography>
              ),
            });
          }
          if (info.medicalRecordNumber) items.push({ label: 'MRN', value: info.medicalRecordNumber });
          if (info.internalId) items.push({ label: 'Patient ID', value: info.internalId });
          if (entitySubId) items.push({ label: 'Entity', value: <Typography component="span" sx={{ fontSize: 'inherit', fontFamily: 'monospace', wordBreak: 'break-all' }}>{entitySubId}</Typography>, span: 'wide' });
          if (info.insuranceProvider || info.insuranceId) {
            const ins = [info.insuranceProvider, info.insuranceId].filter(Boolean).join(' · ');
            items.push({ label: 'Insurance', value: ins });
          }
          const addr = [
            [info.addressLine1, info.addressLine2].filter(Boolean).join(', '),
            [info.city, info.state, info.zipCode].filter(Boolean).join(' '),
          ].filter(Boolean).join(' · ');
          if (addr) items.push({ label: 'Address', value: addr, span: 'wide' });
          if (info.emergencyContactName || info.emergencyContactPhone) {
            const ec = [
              info.emergencyContactName,
              info.emergencyContactRelationship ? `(${info.emergencyContactRelationship})` : '',
              info.emergencyContactPhone,
            ].filter(Boolean).join(' ');
            items.push({ label: 'Emergency', value: ec, span: 'wide' });
          }
          if (info.feeStructure || info.feeAmount != null) {
            const fee = [
              info.feeAmount != null ? `$${Number(info.feeAmount).toFixed(2)}` : '',
              info.feeStructure ? `(${info.feeStructure})` : '',
            ].filter(Boolean).join(' ');
            items.push({ label: 'Fee', value: fee });
          }
          if (info.paymentStatus) items.push({ label: 'Payment', value: info.paymentStatus });
          if (info.nextPaymentDue) items.push({ label: 'Next Due', value: formatDateOnly(String(info.nextPaymentDue), dateTimeFormat) || info.nextPaymentDue });
          if (info.lastVisitDate) {
            const lv = [
              formatDateOnly(String(info.lastVisitDate), dateTimeFormat) || info.lastVisitDate,
              info.lastVisitType ? `(${info.lastVisitType})` : '',
            ].filter(Boolean).join(' ');
            items.push({ label: 'Last Visit', value: lv });
          }
          // Last Activity = the most recent thing on this chart (top History
          // entry or last message) — the one useful field the old bottom
          // "Administrative" card had that wasn't already up here.
          const lastMsg = info?.lastMessageAt || null;
          const topHist = allHistoryEvents[0]?.event_date || allHistoryEvents[0]?.created_at || null;
          const lastActivity = [topHist, lastMsg]
            .filter(Boolean)
            .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())[0];
          if (lastActivity) {
            items.push({ label: 'Last Activity', value: formatDateOnly(String(lastActivity), dateTimeFormat) || String(lastActivity) });
          }
          if (Array.isArray(info.tags) && info.tags.length) {
            // Spruce contact tags arrive as objects of shape
            // `{id, value, object:'contactTag'}` — so the label lives on
            // `.value`, not `.name`/`.label`. Without the `.value` branch
            // every chip renders as "[object Object]". We also drop the
            // auto-generated CSV-import batch markers (e.g.
            // `import_Dec222025_1630`) — they're bulk-import bookkeeping,
            // not clinical categorization, and they crowd out real tags.
            const labels = info.tags
              .map((t: any) =>
                typeof t === 'string' ? t : (t?.value ?? t?.name ?? t?.label ?? ''),
              )
              .map((s: any) => String(s || '').trim())
              .filter((s: string) => s && !/^import_/i.test(s));
            if (labels.length) {
              items.push({
                label: 'Tags',
                span: 'full',
                value: (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                    {labels.slice(0, 8).map((label: string, i: number) => (
                      <Chip
                        key={i}
                        label={label}
                        size="small"
                        variant="outlined"
                        sx={{
                          maxWidth: { xs: 200, sm: 240 },
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                      />
                    ))}
                  </Box>
                ),
              });
            }
          }
          if (!items.length) return null;
          // Stacked label-over-value layout: small uppercase eyebrow on top,
          // value below. Reads better than label-inline when the value is
          // multi-line (addresses, emergency contact). The responsive grid
          // climbs 1 → 2 → 3 → 4 columns; `wide` items span 2 cells from
          // tablet up so the long fields get breathing room; `full` (tags)
          // takes the whole row.
          const spanSx = (span?: 'wide' | 'full') => {
            if (span === 'full') {
              return { gridColumn: '1 / -1' };
            }
            if (span === 'wide') {
              return {
                gridColumn: {
                  xs: 'span 2',
                  sm: 'span 2',
                  md: 'span 2',
                  lg: 'span 2',
                },
              };
            }
            return undefined;
          };
          const clinicalExpanded = !sectionCollapse.collapsed.clinical;
          return (
            // On mobile the identity/contact rows above are hidden, so Additional
            // Info is the first line of the card — drop the separator + top
            // margin that would otherwise leave dead space under the tabs.
            <Box sx={isMobile ? {} : { mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={clinicalExpanded}
                aria-label={`Additional info, ${clinicalExpanded ? 'collapse section' : 'expand section'}`}
                onClick={() => sectionCollapse.toggleSection('clinical')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    sectionCollapse.toggleSection('clinical');
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:focus-visible': {
                    outline: (t) => `2px solid ${t.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: 'text.secondary' }}
                >
                  Additional Info
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {onEditContact && (
                    <IconButton
                      size="small"
                      aria-label="Edit contact, address & administrative details"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditContact();
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {clinicalExpanded
                    ? <ExpandLessIcon sx={{ color: 'action.active' }} />
                    : <ExpandMoreIcon sx={{ color: 'action.active' }} />}
                </Box>
              </Box>
              <Collapse in={clinicalExpanded} timeout="auto" unmountOnExit>
                <Box sx={{
                  // Density pass: 2-up on phones (was 1-up) with tighter gaps.
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  columnGap: { xs: 1, sm: 1.5, md: 2 },
                  rowGap: { xs: 0.75, sm: 1 },
                }}>
              {items.map((it) => (
                <Box
                  key={it.label}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    minWidth: 0,
                    ...spanSx(it.span),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      fontSize: 11,
                      lineHeight: 1.2,
                    }}
                  >
                    {it.label}
                  </Typography>
                  {typeof it.value === 'string' || typeof it.value === 'number' ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.4,
                      }}
                    >
                      {it.value}
                    </Typography>
                  ) : (
                    it.value
                  )}
                </Box>
              ))}
                </Box>
              </Collapse>
            </Box>
          );
        })()}
      </Paper>

      {error && (
        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'error.main', color: 'error.main' }}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}
      </>
      )}

      {/* Practice-Fusion-style overview: Diagnoses + history | Allergies +
          Medications | Encounters. Rendered for 'details'/'all' (Details tab)
          AND 'chart' (the desktop Open Chart drawer — snapshot only, no
          Additional Info). The id is the scroll/flash target for the header's
          Open Chart icon (see Contacts.tsx). */}
      {(view === 'details' || view === 'all' || view === 'chart') && (
      <Box id="patient-chart-snapshot" sx={{ borderRadius: 2 }}>
        <PatientOverview
          spruceEntityId={spruceEntityId}
          details={snapshotDetails}
          encounters={encounters}
          loadingEncounters={loadingClinical}
          onOpenHistory={onOpenHistory}
        />
      </Box>
      )}

      {(view === 'documents' || view === 'all') && (
      <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 1.25, minWidth: 0, width: '100%' }}>
        <Paper sx={{ p: { xs: 1, sm: 1.25 }, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' }}>
          {/* One compact header row: title + info tooltip on the left, the
              Normal/Chart-Styled switch inline on the right (was a title
              block + subtitle + a full Tabs row = ~3 rows of chrome). */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Consolidated Summary</Typography>
              <Tooltip title="Edit either tab. Typing saves immediately. An empty box stays empty and does not revert to the AI summary.">
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {(loadingPatient || loadingSummary) && <CircularProgress size={14} />}
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={summaryView}
              onChange={(_, v) => v !== null && setSummaryView(v)}
              aria-label="Summary view"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1, py: 0.25, fontSize: '0.75rem' } }}
            >
              <ToggleButton value="normal">Normal</ToggleButton>
              <ToggleButton value="chart">Chart-Styled</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {summaryView === 'normal' ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={summaryDraft ? 4 : 2}
                value={summaryDraft}
                onChange={(e) => {
                  const next = e.target.value;
                  setSummaryDraft(next);
                  setLocalNormalOverride(next);
                  void persistSummary('normal', next);
                }}
                placeholder="AI summary will appear here after documents are processed."
              />
              <Typography variant="caption" color="text.secondary">
                {savingSummary && summaryView === 'normal' ? 'Saving…' : hasManualSummary ? 'Manual override active' : ''}
              </Typography>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={chartDraft ? 4 : 2}
                value={chartDraft}
                onChange={(e) => {
                  const next = e.target.value;
                  setChartDraft(next);
                  setLocalChartOverride(next);
                  void persistSummary('chart', next);
                }}
                placeholder="Chart-styled summary will appear here after documents are processed."
              />
              <Typography variant="caption" color="text.secondary">
                {savingSummary && summaryView === 'chart' ? 'Saving…' : hasManualChartSummary ? 'Manual override active' : ''}
              </Typography>
            </>
          )}

        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 1.25 }, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={handleUploadClick}
                disabled={uploading || pasting || recordUploading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MicIcon />}
                onClick={handleOpenRecord}
                disabled={uploading || pasting || recordUploading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Speech-to-Text
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TextSnippetIcon />}
                onClick={handleOpenPaste}
                disabled={uploading || pasting || recordUploading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Paste Text
              </Button>
            </Box>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUpload} />
          </Box>
          {(uploading || pasting || recordUploading) && <LinearProgress />}
          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
              {mergedDocuments.length === 0 && (
                <ListItemText sx={{ p: 2 }} primary="No documents uploaded yet." />
              )}
              {mergedDocuments.map((doc) => {
                // Legacy "manual" rows come back from the API as uploaded_by=null
                // so we can skip the "Uploaded by ..." clause entirely instead
                // of showing "by manual".
                const uploader = (doc.uploaded_by || '').trim();
                const status = doc.status || 'uploaded';
                const isExternal = (doc as any)._source === 'external';
                const prefix = isExternal ? 'External' : status;
                const uploadedAt = doc.created_at || (doc as { createdAt?: string }).createdAt;
                const dateLabel = formatDateOnly(uploadedAt, dateTimeFormat);
                const secondary = [prefix, dateLabel, uploader ? `Uploaded by ${uploader}` : '']
                  .filter(Boolean)
                  .join(' • ');
                return (
                  <ListItemButton key={doc.id} selected={doc.id === selectedDocId} onClick={() => setSelectedDocId(doc.id)}>
                    <DescriptionIcon sx={{ mr: 1 }} />
                    <ListItemText
                      sx={{ minWidth: 0 }}
                      primary={doc.filename}
                      secondary={secondary}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
          {/* Whole section hidden until at least one document exists \u2014 an
              empty patient shouldn't carry a divider + heading + hint for a
              summary that can't exist yet. */}
          {mergedDocuments.length > 0 && (
          <>
          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Selected Document Summary</Typography>
            {mergedDocuments.some((d) => (d as any)._source !== 'external') && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleSummarizeAllDocuments}
                disabled={summarizingAll}
                startIcon={summarizingAll ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {summarizingAll ? 'Summarizing\u2026' : 'Summarize all'}
              </Button>
            )}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {selectedDoc?.summary_text || 'Select a document to view its AI summary.'}
          </Typography>
          {selectedDoc && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* External docs come with a pre-signed download_url straight
                  from the levitanai_contacts service — we link to it
                  directly instead of routing through the local download
                  endpoint, which only knows about local patient_documents.
                  A null or already-expired link disables both buttons so
                  the user gets immediate feedback instead of an upstream
                  403/404. */}
              {(() => {
                const sd: any = selectedDoc;
                const isExt = sd._source === 'external';
                const extDead = isExt && (!sd._downloadUrl || externalLinkExpired);
                return (
                  <>
                    <Button
                      variant="outlined"
                      href={
                        isExt
                          ? (sd._downloadUrl || '#')
                          : `/api/patient-details/patient/${spruceEntityId}/documents/${selectedDoc.id}/download?inline=1`
                      }
                      target="_blank"
                      rel="noreferrer"
                      disabled={extDead}
                    >
                      View
                    </Button>
                    <Button
                      variant="outlined"
                      href={
                        isExt
                          ? (sd._downloadUrl || '#')
                          : `/api/patient-details/patient/${spruceEntityId}/documents/${selectedDoc.id}/download`
                      }
                      target="_blank"
                      rel="noreferrer"
                      disabled={extDead}
                    >
                      Download
                    </Button>
                    {!isExt && (
                      <Button
                        variant="contained"
                        onClick={() => handleSummarizeDocument(selectedDoc.id, !!selectedDoc.summary_text)}
                        disabled={summarizingDocId === selectedDoc.id}
                        startIcon={summarizingDocId === selectedDoc.id ? <CircularProgress size={16} color="inherit" /> : undefined}
                      >
                        {summarizingDocId === selectedDoc.id
                          ? 'Reading\u2026'
                          : (selectedDoc.summary_text ? 'Re-summarize' : 'Summarize')}
                      </Button>
                    )}
                    {extDead && (
                      <Typography variant="caption" color="text.secondary">
                        {externalLinkExpired ? 'External link expired' : 'No download link available'}
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Box>
          )}
          </>
          )}
        </Paper>
      </Box>

      <Dialog open={pasteOpen} onClose={handleClosePaste} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>Paste Text for Summary</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Title (optional)"
              value={pasteTitle}
              onChange={(e) => setPasteTitle(e.target.value)}
            />
            <TextField
              label="Date (optional)"
              type="date"
              value={pasteDate}
              onChange={(e) => setPasteDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Paste text"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            multiline
            minRows={6}
          />
          <Typography variant="caption" color="text.secondary">
            Reminder: Paste only clinically relevant content. Verify accuracy and avoid unnecessary PHI.
          </Typography>
          {pasteError && (
            <Typography variant="caption" color="error">
              {pasteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaste} disabled={pasting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmitPaste} disabled={pasting || !pasteText.trim()}>
            {pasting ? 'Saving...' : 'Summarize + Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recordOpen} onClose={handleCloseRecord} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>Speech-to-Text</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Title (optional)"
              value={recordTitle}
              onChange={(e) => setRecordTitle(e.target.value)}
            />
            <TextField
              label="Date (optional)"
              type="date"
              value={recordDate}
              onChange={(e) => setRecordDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={recording ? 'error.main' : 'text.secondary'}>
              {recording ? `Recording ${formatDuration(recordSeconds)}` : `Recorded ${formatDuration(recordSeconds)}`}
            </Typography>
            <Button variant="outlined" onClick={handleStartRecording} disabled={recording || recordUploading}>
              Start Recording
            </Button>
            <Button variant="outlined" onClick={handleStopRecording} disabled={!recording}>
              Stop
            </Button>
            {recordedBlob && !recording && (
              <Typography variant="caption" color="text.secondary">
                Recording ready to upload
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Max recording length: 60 minutes.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reminder: Record only clinically relevant content. Verify accuracy and avoid unnecessary PHI.
          </Typography>
          {recordError && (
            <Typography variant="caption" color="error">
              {recordError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecord} disabled={recording || recordUploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadRecording}
            disabled={!recordedBlob || recording || recordUploading}
          >
            {recordUploading ? 'Uploading...' : 'Upload + Summarize'}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      {(view === 'history' || view === 'all') && (
      <Paper sx={{ p: { xs: 1, sm: 1.25 }, minWidth: 0 }}>
        {/* No "History" header / collapse toggle / "Timeline" label — the tab
            the user tapped already says History, so the page opens straight
            at the filter chips (density pass). */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                {historyEvents.length > 0 && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={historyFilter}
                    onChange={(_, value) => {
                      if (value) setHistoryFilter(value);
                    }}
                    aria-label="Filter timeline events"
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      rowGap: 0.5,
                      // When the group wraps, MUI's default "remove inner
                      // borders" rule leaves the first button of each new
                      // row missing a left border. Restore it so wrapped
                      // rows still look like a connected button group.
                      '& .MuiToggleButtonGroup-grouped': {
                        borderRadius: 1,
                        '&:not(:first-of-type)': {
                          borderLeft: '1px solid',
                          borderColor: 'divider',
                          marginLeft: 0,
                        },
                      },
                    }}
                  >
                    <ToggleButton value="all" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      All ({nonAiHistoryCount})
                    </ToggleButton>
                    {TIMELINE_SOURCES.filter(
                      (s) => s.core || (historyCounts[s.key] || 0) > 0,
                    ).map((s) => (
                      <ToggleButton
                        key={s.key}
                        value={s.key}
                        sx={{ py: 0.25, px: 1, textTransform: 'none' }}
                      >
                        {s.label} ({historyCounts[s.key] || 0})
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              </Box>
              {historyEvents.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search timeline (messages, edits, appointments)…"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: historySearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setHistorySearch('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={historyDatePreset}
                    onChange={(_, value) => {
                      if (value) setHistoryDatePreset(value);
                    }}
                    aria-label="Filter timeline by date range"
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      rowGap: 0.5,
                      '& .MuiToggleButtonGroup-grouped': {
                        borderRadius: 1,
                        '&:not(:first-of-type)': {
                          borderLeft: '1px solid',
                          borderColor: 'divider',
                          marginLeft: 0,
                        },
                      },
                    }}
                  >
                    <ToggleButton value="all" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      All time
                    </ToggleButton>
                    <ToggleButton value="30d" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      30d
                    </ToggleButton>
                    <ToggleButton value="90d" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      90d
                    </ToggleButton>
                    <ToggleButton value="ytd" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      YTD
                    </ToggleButton>
                    <Tooltip
                      title={
                        lastVisitDate
                          ? `Since ${formatEventDate(lastVisitDate.toISOString())}`
                          : 'No past visits on record'
                      }
                    >
                      {/* span wrapper lets Tooltip work on a disabled button */}
                      <span>
                        <ToggleButton
                          value="since_visit"
                          disabled={!lastVisitDate}
                          sx={{ py: 0.25, px: 1, textTransform: 'none' }}
                        >
                          Since last visit
                        </ToggleButton>
                      </span>
                    </Tooltip>
                    <ToggleButton value="custom" sx={{ py: 0.25, px: 1, textTransform: 'none' }}>
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {historyDatePreset === 'custom' && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <TextField
                        size="small"
                        type="date"
                        label="From"
                        value={historyCustomStart}
                        onChange={(e) => setHistoryCustomStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: historyCustomEnd || undefined }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="To"
                        value={historyCustomEnd}
                        onChange={(e) => setHistoryCustomEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: historyCustomStart || undefined }}
                      />
                      {(historyCustomStart || historyCustomEnd) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setHistoryCustomStart('');
                            setHistoryCustomEnd('');
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {loadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : filteredHistoryEvents.length ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {visibleHistoryEvents.map((event) => {
                    // Compact AI consultation marker — a muted, dashed card that
                    // records that an AI consultation thread exists, never the AI
                    // prose itself. "Open" jumps to the AI Consultation tab and
                    // selects this thread.
                    if (event.event_source === 'ai') {
                      const threadId = event.metadata?.threadId as string | undefined;
                      return (
                        <Box
                          key={event.id}
                          sx={{
                            border: '1px dashed',
                            borderColor: 'grey.400',
                            borderLeft: '3px dashed',
                            borderLeftColor: 'secondary.main',
                            borderRadius: 1,
                            p: 1.5,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <SmartToyIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatEventDate(event.event_date || event.created_at)}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: 'secondary.main',
                                border: '1px solid',
                                borderColor: 'secondary.main',
                                borderRadius: 0.5,
                                px: 0.5,
                                py: 0.1,
                                lineHeight: 1.4,
                              }}
                            >
                              AI
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            {threadId && onOpenConsultation && (
                              <Button
                                size="small"
                                variant="text"
                                color="secondary"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                onClick={() => onOpenConsultation(threadId)}
                                sx={{ textTransform: 'none', py: 0 }}
                              >
                                Open
                              </Button>
                            )}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word', fontStyle: 'italic' }}
                          >
                            {event.event_title || 'AI consultation'}
                            {event.event_body && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.75, fontWeight: 400, fontStyle: 'normal' }}
                              >
                                · {event.event_body}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      );
                    }
                    const source = event.event_source || 'unknown';
                    // Hide legacy "manual" attribution — pre-attribution rows
                    // stored the literal string and would otherwise read as
                    // "by manual" instead of an actual clinician's name.
                    const rawAuthor = (event.author || '').trim();
                    const author = rawAuthor && rawAuthor.toLowerCase() !== 'manual' ? rawAuthor : '';
                    const sourceCfg = TIMELINE_SOURCE_MAP[source];
                    const sourceColor = sourceCfg?.color || 'grey.500';
                    // Chart-edit accent palette — keyed to the same colors
                    // the Clinical Strip pills use, so the left border on
                    // the timeline card matches the chip that produced it
                    // at a glance: Allergies = red, Primary DX = indigo,
                    // Current Meds = amber, Family History = emerald, any
                    // Vitals_* = teal.
                    const chartCategoryAccent = (cat?: string): string | null => {
                      if (!cat) return null;
                      if (cat === 'allergies') return '#dc2626';
                      if (cat === 'primary_diagnosis') return '#4f46e5';
                      if (cat === 'current_medications') return '#d97706';
                      if (cat === 'family_history') return '#059669';
                      if (cat.startsWith('vitals_')) return '#0d9488';
                      return null;
                    };
                    const chartAccent = source === 'chart' ? chartCategoryAccent(event.event_category) : null;
                    const accentColor =
                      chartAccent
                      ?? (source === 'chart' ? 'info.light'
                      : source === 'spruce' ? 'primary.light'
                      : source === 'calendar' ? 'success.light'
                      : 'grey.200');
                    return (
                      <Box
                        key={event.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderLeft: '3px solid',
                          borderLeftColor: accentColor,
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatEventDate(event.event_date || event.created_at)}
                          </Typography>
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              color: sourceColor,
                              border: '1px solid',
                              borderColor: sourceColor,
                              borderRadius: 0.5,
                              px: 0.5,
                              py: 0.1,
                              lineHeight: 1.4,
                            }}
                          >
                            {sourceCfg?.badgeLabel || source}
                          </Box>
                          {/* Provenance badge: this clinical entry graduated
                              from an AI-assisted action (e.g. an appointment
                              created from an AI scheduling suggestion). */}
                          {event.ai_assisted && (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                color: 'secondary.main',
                                border: '1px solid',
                                borderColor: 'secondary.main',
                                borderRadius: 0.5,
                                px: 0.5,
                                py: 0.1,
                                lineHeight: 1.4,
                              }}
                            >
                              <SmartToyIcon sx={{ fontSize: '0.8rem' }} />
                              AI-assisted
                            </Box>
                          )}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          {event.event_title || event.event_type || 'History Event'}
                          {author && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.75, fontWeight: 400 }}
                            >
                              by {author}
                            </Typography>
                          )}
                        </Typography>
                        {event.event_body && (() => {
                          const displayBody = formatVitalBody(event.event_category, event.event_body);
                          const fullRaw = event.event_body_full;
                          const displayFull = fullRaw
                            ? formatVitalBody(event.event_category, fullRaw)
                            : undefined;
                          const isTruncated = !!displayFull && displayFull !== displayBody;
                          const body = (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                // Long unbreakable strings (URLs, IDs, hex
                                // tokens) would otherwise push the timeline
                                // card wider than the phone screen and
                                // trigger a horizontal scroll on the whole
                                // chart tab.
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                              }}
                            >
                              {displayBody}
                            </Typography>
                          );
                          return isTruncated ? (
                            <Tooltip title={displayFull} placement="top-start" arrow>
                              <Box sx={{ cursor: 'help' }}>{body}</Box>
                            </Tooltip>
                          ) : body;
                        })()}
                      </Box>
                    );
                  })}
                  {filteredHistoryEvents.length > HISTORY_PAGE_SIZE && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        pt: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Showing {visibleHistoryEvents.length} of {filteredHistoryEvents.length}
                      </Typography>
                      {historyLimit < filteredHistoryEvents.length && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            onClick={() =>
                              setHistoryLimit((n) => n + HISTORY_PAGE_SIZE)
                            }
                            sx={{ textTransform: 'none' }}
                          >
                            Show more (+{Math.min(
                              HISTORY_PAGE_SIZE,
                              filteredHistoryEvents.length - historyLimit,
                            )})
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              setHistoryLimit(filteredHistoryEvents.length)
                            }
                            sx={{ textTransform: 'none' }}
                          >
                            Show all ({filteredHistoryEvents.length})
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : historyEvents.length ? (
                <Typography variant="body2" color="text.secondary">
                  No events match this filter.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No history events yet. Imports and lab captures will appear here.
                </Typography>
              )}
            </Box>
        </Box>
      </Paper>
      )}
    </Box>
  );
};

export default PatientDetailsView;
